/* eslint-disable max-lines */
import { Button, Card, CardHeader, CardBody, FormGroup, Form, Input, Container, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import SupportService from '../../../services/Support';
import { Link } from 'react-router-dom';
import SchoolService from '../../../services/School';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';
import history from '../../../history';
import isAdmin from '../../../helpers/isAdmin';
import isParent from '../../../helpers/isParent';
import type { SupportSubcategoryQuestion } from './taxonomies';
import {
  SUPPORT_DEPARTMENTS,
  getCategoriesByDepartment,
  getSubcategoriesByCategory,
  getQuestionsBySubcategory,
  isQuestionVisible,
  SupportQuestionAnswerItem,
  SHIRT_SUBCATEGORY_PREFIXES,
  buildShirtsPayloadFromAnswers,
} from './taxonomies';
import DateTime from '../../../components/Core/DateTime';
import { SHIRT_SIZE_KEYS } from './taxonomies/shirt-sizes';
import { findOversizedAttachment, useSupportAttachmentConfig } from '../../../hooks/useSupportAttachmentConfig';

function chunkTaxonomyQuestions(questions: SupportSubcategoryQuestion[]): SupportSubcategoryQuestion[][] {
  const chunks: SupportSubcategoryQuestion[][] = [];
  const sizeRunLen = SHIRT_SIZE_KEYS.length;
  let i = 0;
  while (i < questions.length) {
    let isSizeRun = true;
    for (let j = 0; j < sizeRunLen && isSizeRun; j++) {
      const q = questions[i + j];
      if (!q || q.type !== 'text' || !q.id.endsWith('-' + SHIRT_SIZE_KEYS[j])) isSizeRun = false;
    }
    if (isSizeRun && i + sizeRunLen <= questions.length) {
      chunks.push(questions.slice(i, i + sizeRunLen));
      i += sizeRunLen;
    } else {
      chunks.push([questions[i]]);
      i += 1;
    }
  }
  return chunks;
}

const SupportNew = () => {
  const { token, currentSchool, user }: any = useContext(AuthContext);
  const isAdminUser = isAdmin(user);
  const canCreateSupportTicketsWithOtherSchools =
    isAdmin(user) || user.employee.canCreateSupportTicketsWithOtherSchools;
  const isParentUser = isParent(user);

  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schoolId, setSchoolId] = useState('');
  const [name, setName] = useState(`${user.firstName} ${user.lastname || ''}`);
  const [email, setEmail] = useState(user.email);
  const [department, setDepatment] = useState(isParentUser ? 'App' : '');
  const [priority, setPriority] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [ticketDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});
  const [questionFiles, setQuestionFiles] = useState<Record<string, File[]>>({});
  const { maxSizeMb, accept } = useSupportAttachmentConfig(schoolId);

  const [selectList, setSelectList] = useState<any[]>([]);

  const listSelectSchool = async () => {
    try {
      const filteredOptions = {
        filters: [] as any[],
        options: createOptionsPage(createPagedMeta({ perPage: 999999 })),
      };
      const result = await SchoolService(token).list(filteredOptions);
      const schools = (result.data || [])
        .map((school: any) => ({ id: school.id, name: school.name }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      setSelectList(schools);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No Schools found');
    }
  };

  useEffect(() => {
    setIsDisabled(false);
    listSelectSchool();
  }, []);

  useEffect(() => {
    setSchoolId(currentSchool.value || '');
  }, [currentSchool]);

  useEffect(() => {
    setCategory('');
    setSubcategory('');
    setQuestionAnswers({});
    setQuestionFiles({});
  }, [department]);

  useEffect(() => {
    setSubcategory('');
    setQuestionAnswers({});
    setQuestionFiles({});
  }, [category]);

  useEffect(() => {
    if (!category) return;
    const subs = getSubcategoriesByCategory(category);
    if (subs.length === 1) {
      setSubcategory(subs[0].id);
    }
  }, [category]);

  useEffect(() => {
    setQuestionAnswers({});
    setQuestionFiles({});
  }, [subcategory]);

  const taxonomyCategories = getCategoriesByDepartment(department);
  const taxonomySubcategories = getSubcategoriesByCategory(category);
  const taxonomyQuestions = getQuestionsBySubcategory(subcategory);
  const visibleTaxonomyQuestions = useMemo(
    () => taxonomyQuestions.filter((q) => isQuestionVisible(q, questionAnswers)),
    [taxonomyQuestions, questionAnswers],
  );
  const hasDynamicFormContent = !!subcategory && taxonomyQuestions.length > 0;

  const setQuestionAnswer = (questionId: string, value: string) => {
    setQuestionAnswers((prev) => {
      const next = { ...prev, [questionId]: value };
      for (const q of taxonomyQuestions) {
        if (q.visibleWhen?.questionId === questionId && value !== q.visibleWhen.value) {
          delete next[q.id];
        }
      }
      return next;
    });
  };

  const setQuestionFile = (questionId: string, files: File[]) => {
    const oversizedFile = findOversizedAttachment(files, maxSizeMb);
    if (oversizedFile) {
      toast.warn(`"${oversizedFile.name}" is larger than the ${maxSizeMb}MB limit per file.`);
      return;
    }

    if (files.length > 0) {
      setQuestionFiles((prev) => ({ ...prev, [questionId]: files }));
      setQuestionAnswers((prev) => ({ ...prev, [questionId]: files.map((file) => file.name).join(', ') }));
    } else {
      setQuestionFiles((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setQuestionAnswers((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setIsDisabled(false);

    const body: any = {
      schoolId,
      name,
      email,
      department,
      type: taxonomyCategories.find((c) => c.id === category)?.label ?? undefined,
      priority,
      description,
      subject,
      date: ticketDate,
      category: category ? taxonomyCategories.find((c) => c.id === category)?.label ?? undefined : undefined,
      subcategory: subcategory
        ? taxonomySubcategories.find((s) => s.id === subcategory)?.label ?? undefined
        : undefined,
    };

    const shirtPrefix = subcategory ? SHIRT_SUBCATEGORY_PREFIXES[subcategory] : undefined;

    if (subcategory && taxonomyQuestions.length > 0) {
      const qaArray: SupportQuestionAnswerItem[] = visibleTaxonomyQuestions
        .filter((q) => q.type !== 'info')
        .filter((q) => {
          if (!shirtPrefix) return true;
          const answer = questionAnswers[q.id];
          return typeof answer === 'string' && answer.trim() !== '';
        })
        .map((q) => ({
          question: q.label,
          answer: questionAnswers[q.id] ?? '',
        }));
      body.questionAnswers = qaArray;
    }

    if (shirtPrefix) {
      body.shirts = buildShirtsPayloadFromAnswers(questionAnswers, shirtPrefix);
    }

    try {
      const result = await SupportService(token).create(body);
      const filesToUpload = Object.values(questionFiles).flat();
      if (filesToUpload.length > 0) {
        const formData = new FormData();
        filesToUpload.forEach((file) => {
          formData.append('attachments', file);
        });
        await SupportService(token).attachment(result?.data?.id, formData);
      }
      toast.success('Your ticket has been submitted successfully!');
      setLoading(false);
      if (isAdminUser) {
        return history.push('/support');
      } else {
        setDepatment(isParentUser ? 'App' : '');
        setPriority('');
        setSubject('');
        setDescription('');
        setCategory('');
        setSubcategory('');
        setQuestionAnswers({});
        setQuestionFiles({});
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Create error!');
      toast.error(message);
    }

    setLoading(false);
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col className="order-xl-2" xl="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Create new Support Ticket</h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    {canCreateSupportTicketsWithOtherSchools && (
                      <Link to="/support/">
                        <Button color="primary" className="float-right">
                          Back
                        </Button>
                      </Link>
                    )}
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <div className="pl-lg-4">
                    {canCreateSupportTicketsWithOtherSchools && (
                      <>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="input-school-id">
                                School
                              </label>
                              <Input
                                type="select"
                                onChange={(e) => setSchoolId(e.target.value)}
                                value={schoolId}
                                disabled={isDisabled || selectList.length <= 1}
                              >
                                {selectList?.map((filter: any, index: number) => {
                                  return (
                                    <option value={filter.id} key={`filter-button-${index}`}>
                                      {filter.name}
                                    </option>
                                  );
                                })}
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="input-date">
                                Date
                              </label>
                              <Input
                                type="text"
                                id="input-date"
                                value={ticketDate}
                                readOnly
                                disabled
                                className="bg-light"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="input-school-id">
                                Name
                              </label>
                              <Input type="text" onChange={(e) => setName(e.target.value)} value={name} disabled />
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="input-school-id">
                                Email
                              </label>
                              <Input type="text" onChange={(e) => setEmail(e.target.value)} value={email} disabled />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                    <Row>
                      <Col lg={taxonomySubcategories.length > 1 ? 4 : 6}>
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-department">
                            Department
                          </label>
                          <select
                            name="input-department"
                            id="input-department"
                            className="form-control"
                            disabled={isParentUser || isDisabled}
                            value={department}
                            onChange={(e) => setDepatment(e.target.value)}
                            required
                          >
                            {!isParentUser && (
                              <option value="" className="text-muted" disabled>
                                Select a Department
                              </option>
                            )}
                            {SUPPORT_DEPARTMENTS.filter((d) => (isParentUser ? d.id === 'App' : true)).map((d) => (
                              <option value={d.id} key={d.id}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      <Col lg={taxonomySubcategories.length > 1 ? 4 : 6}>
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-category">
                            Category
                          </label>
                          <select
                            name="input-category"
                            id="input-category"
                            className="form-control"
                            disabled={isDisabled}
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            required
                          >
                            <option value="" className="text-muted" disabled>
                              Select a Category
                            </option>
                            {taxonomyCategories.map((item) => (
                              <option value={item.id} key={item.id}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                      {taxonomySubcategories.length > 1 && (
                        <Col lg="4">
                          <FormGroup>
                            <label className="form-control-label" htmlFor="input-subcategory">
                              Subcategory
                            </label>
                            <select
                              name="input-subcategory"
                              id="input-subcategory"
                              className="form-control"
                              disabled={isDisabled || !category}
                              value={subcategory}
                              onChange={(e) => setSubcategory(e.target.value)}
                              required
                            >
                              <option value="" className="text-muted" disabled>
                                Select a Subcategory
                              </option>
                              {taxonomySubcategories.map((item) => (
                                <option value={item.id} key={item.id}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </FormGroup>
                        </Col>
                      )}
                    </Row>
                  </div>

                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-priority">
                            Priority
                          </label>
                          <select
                            name="input-priority"
                            id="input-priority"
                            className="form-control"
                            disabled={isDisabled}
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                            required
                          >
                            <option value="" disabled className="text-muted">
                              Select a Priority
                            </option>
                            <option value="LOW">Low</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HIGH">High</option>
                          </select>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <div className="pl-lg-4">
                    {hasDynamicFormContent && (
                      <Row>
                        <Col lg="12">
                          {chunkTaxonomyQuestions(visibleTaxonomyQuestions).map((chunk, chunkIdx) =>
                            chunk.length === SHIRT_SIZE_KEYS.length ? (
                              <Row key={`size-row-${chunkIdx}`} className="mb-3">
                                {chunk.map((q, idx) => (
                                  <Col key={q.id} xs="6" sm="4" md={2} lg={1}>
                                    <FormGroup>
                                      <label className="form-control-label" htmlFor={`q-${q.id}`}>
                                        {SHIRT_SIZE_KEYS[idx].toUpperCase()}
                                      </label>
                                      <Input
                                        type="text"
                                        id={`q-${q.id}`}
                                        value={questionAnswers[q.id] ?? ''}
                                        onChange={(e) => setQuestionAnswer(q.id, e.target.value.replace(/[^0-9]/g, ''))}
                                        disabled={isDisabled}
                                        required={q.required}
                                        placeholder="0"
                                      />
                                    </FormGroup>
                                  </Col>
                                ))}
                              </Row>
                            ) : (
                              chunk.map((q) => (
                                <FormGroup key={q.id}>
                                  {q.type !== 'info' && (
                                    <label className="form-control-label" htmlFor={`q-${q.id}`}>
                                      {q.label}
                                      {q.required && ' *'}
                                    </label>
                                  )}
                                  {q.subtitle && (
                                    <small
                                      className="form-text text-muted d-block mb-1"
                                      style={{ whiteSpace: 'pre-line' }}
                                    >
                                      {q.subtitle}
                                    </small>
                                  )}
                                  {q.type === 'info' && (
                                    <p className="text-muted mb-2" style={{ whiteSpace: 'pre-line' }}>
                                      {q.label}
                                    </p>
                                  )}
                                  {q.link && (
                                    <a
                                      className="form-text d-block mb-1"
                                      href={q.link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {q.link.label}
                                    </a>
                                  )}
                                  {q.type === 'text' && (
                                    <Input
                                      type="text"
                                      id={`q-${q.id}`}
                                      value={questionAnswers[q.id] ?? ''}
                                      onChange={(e) => setQuestionAnswer(q.id, e.target.value)}
                                      disabled={isDisabled}
                                      required={q.required}
                                    />
                                  )}
                                  {q.type === 'textarea' && (
                                    <textarea
                                      className="form-control"
                                      id={`q-${q.id}`}
                                      value={questionAnswers[q.id] ?? ''}
                                      onChange={(e) => setQuestionAnswer(q.id, e.target.value)}
                                      disabled={isDisabled}
                                      required={q.required}
                                    />
                                  )}
                                  {q.type === 'select' && (
                                    <Input
                                      type="select"
                                      id={`q-${q.id}`}
                                      value={questionAnswers[q.id] ?? ''}
                                      onChange={(e) => setQuestionAnswer(q.id, e.target.value)}
                                      disabled={isDisabled}
                                      required={q.required}
                                    >
                                      <option value="">Select...</option>
                                      {(q.options ?? []).map((opt) => (
                                        <option value={opt.value} key={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Input>
                                  )}
                                  {q.type === 'date' && (
                                    <DateTime
                                      onChange={(newValue) => {
                                        if (!newValue) {
                                          setQuestionAnswer(q.id, '');
                                          return;
                                        }
                                        const m = moment(newValue);
                                        setQuestionAnswer(
                                          q.id,
                                          m.isValid() ? m.format('YYYY-MM-DD') : String(newValue),
                                        );
                                      }}
                                      value={(() => {
                                        const raw = questionAnswers[q.id];
                                        if (!raw) return '';
                                        const m = moment(raw);
                                        return m.isValid() ? m.format('MM/DD/YYYY') : '';
                                      })()}
                                      inputProps={{
                                        id: `q-${q.id}`,
                                        disabled: isDisabled,
                                        required: q.required,
                                      }}
                                    />
                                  )}
                                  {q.type === 'file' && (
                                    <>
                                      <Input
                                        type="file"
                                        id={`q-${q.id}`}
                                        multiple
                                        accept={accept}
                                        disabled={isDisabled}
                                        onChange={(e) => {
                                          const files = Array.from(e.target.files || []);
                                          setQuestionFile(q.id, files);
                                        }}
                                      />
                                      {questionAnswers[q.id] && (
                                        <span className="ml-2 text-muted small">{questionAnswers[q.id]}</span>
                                      )}
                                    </>
                                  )}
                                </FormGroup>
                              ))
                            ),
                          )}
                        </Col>
                      </Row>
                    )}

                    <Button className="my-4" color="primary" type="submit" disabled={isDisabled}>
                      Submit
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SupportNew;
